import React from 'react';
import { useListContext } from 'react-admin';
import { Box, Chip } from '@mui/material';
import { Today, Schedule, CalendarMonth, DateRange } from '@mui/icons-material';

type TimePeriod = 'today' | '24h' | 'week' | 'month';

const QuickDateFiltersPanel = () => {
  const { filterValues, setFilters } = useListContext();

  const getDateRange = (period: TimePeriod): { gte: Date; lte: Date } => {
    const now = new Date();
    const endOfToday = new Date(now);
    endOfToday.setHours(23, 59, 59, 999);

    switch (period) {
      case 'today': {
        const startOfToday = new Date(now);
        startOfToday.setHours(0, 0, 0, 0);
        return { gte: startOfToday, lte: endOfToday };
      }
      case '24h': {
        const last24h = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        return { gte: last24h, lte: now };
      }
      case 'week': {
        const startOfToday = new Date(now);
        startOfToday.setHours(0, 0, 0, 0);
        const weekAgo = new Date(startOfToday);
        weekAgo.setDate(startOfToday.getDate() - 7);
        return { gte: weekAgo, lte: endOfToday };
      }
      case 'month': {
        const startOfToday = new Date(now);
        startOfToday.setHours(0, 0, 0, 0);
        const monthAgo = new Date(startOfToday);
        monthAgo.setDate(startOfToday.getDate() - 30);
        return { gte: monthAgo, lte: endOfToday };
      }
      default: {
        const _exhaustive: never = period;
        return _exhaustive;
      }
    }
  };

  const handleQuickFilter = (period: TimePeriod) => {
    const dateRange = getDateRange(period);
    setFilters({
      ...filterValues,
      createdBetween: dateRange,
    }, {});
  };

  const clearDateFilter = () => {
    const { createdBetween, ...restFilters } = filterValues;
    setFilters(restFilters, {});
  };

  const isActive = (period: TimePeriod): boolean => {
    if (!filterValues.createdBetween) return false;

    const { gte, lte } = filterValues.createdBetween;
    if (!gte || !lte) return false;

    const expectedRange = getDateRange(period);
    if (!expectedRange?.gte || !expectedRange?.lte) return false;

    const gteTime = new Date(gte).getTime();
    const lteTime = new Date(lte).getTime();
    const expectedGteTime = expectedRange.gte.getTime();
    const expectedLteTime = expectedRange.lte.getTime();

    // Allow 1-minute tolerance for time drift
    const tolerance = 60 * 1000;
    return (
      Math.abs(gteTime - expectedGteTime) < tolerance &&
      Math.abs(lteTime - expectedLteTime) < tolerance
    );
  };

  const hasDateFilter = Boolean(filterValues.createdBetween);

  return (
    <Box sx={{ mb: 1, p: 1 }}>
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Chip
          icon={<Today fontSize="small" />}
          label="Today"
          onClick={() => handleQuickFilter('today')}
          color={isActive('today') ? 'primary' : 'default'}
          variant={isActive('today') ? 'filled' : 'outlined'}
          size="small"
          sx={{
            fontWeight: isActive('today') ? 600 : 400,
          }}
        />
        <Chip
          icon={<Schedule fontSize="small" />}
          label="Last 24h"
          onClick={() => handleQuickFilter('24h')}
          color={isActive('24h') ? 'primary' : 'default'}
          variant={isActive('24h') ? 'filled' : 'outlined'}
          size="small"
          sx={{
            fontWeight: isActive('24h') ? 600 : 400,
          }}
        />
        <Chip
          icon={<DateRange fontSize="small" />}
          label="Past 7 Days"
          onClick={() => handleQuickFilter('week')}
          color={isActive('week') ? 'primary' : 'default'}
          variant={isActive('week') ? 'filled' : 'outlined'}
          size="small"
          sx={{
            fontWeight: isActive('week') ? 600 : 400,
          }}
        />
        <Chip
          icon={<CalendarMonth fontSize="small" />}
          label="Past 30 Days"
          onClick={() => handleQuickFilter('month')}
          color={isActive('month') ? 'primary' : 'default'}
          variant={isActive('month') ? 'filled' : 'outlined'}
          size="small"
          sx={{
            fontWeight: isActive('month') ? 600 : 400,
          }}
        />
        {hasDateFilter && (
          <Chip
            label="Clear"
            onClick={clearDateFilter}
            variant="outlined"
            size="small"
            sx={{
              borderStyle: 'dashed',
              fontWeight: 500,
            }}
          />
        )}
      </Box>
    </Box>
  );
};

export default QuickDateFiltersPanel;
